import { useMemo, useState, type MouseEvent, type ReactNode } from 'react';

export type Column<Row> = {
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  className?: string;
  // When true, the column header becomes a clickable sort trigger.
  // First click sorts ascending, second click descending, third
  // click clears the sort. sortValue is invoked per row to derive
  // the comparable — defaults to String(render(row)), which is
  // brittle for cells that render JSX (badges, chips), so callers
  // rendering non-text cells should supply an explicit sortValue.
  sortable?: boolean;
  sortValue?: (row: Row) => string | number | null | undefined;
};

type Props<Row> = {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  emptyMessage?: ReactNode;
  caption?: string;
  // When set, rows render as cursor-pointer hover targets and clicking
  // anywhere on a row invokes the callback. For keyboard accessibility,
  // callers should also expose a focusable Link inside one of the
  // column renderers (TanStack Router <Link> is the canonical choice).
  onRowClick?: (row: Row) => void;
};

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

function compare(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  // Nulls / undefineds sort to the end so a partial-data column
  // doesn't push the useful rows off-screen.
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

// Minimal presentational table. No sorting, no virtualization. Headers
// carry scope="col" for screen readers; rows get a stable key from the
// caller.
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  emptyMessage = 'No rows.',
  caption,
  onRowClick,
}: Props<Row>): JSX.Element {
  const [sort, setSort] = useState<SortState>(null);

  // Client-side sort. Only re-sorts when either the rows array or
  // the sort state changes — most page transitions swap the rows
  // reference so this stays cheap.
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortable) return rows;
    const getValue = col.sortValue;
    const copy = rows.slice();
    copy.sort((a, b) => {
      const av = getValue ? getValue(a) : String(col.render(a) ?? '');
      const bv = getValue ? getValue(b) : String(col.render(b) ?? '');
      const cmp = compare(av, bv);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  const cycleSort = (key: string): void => {
    // asc → desc → cleared. Third click on the same header clears
    // the sort entirely so the caller's original ordering is
    // preserved.
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-border bg-bg-soft px-4 py-8 text-center text-sm text-ink-2">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border bg-bg">
      <table className="w-full text-left text-xs">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {/* Header uses --p-text-faint (LYN ink-3 = #9CA3AF) to match
            CNC's very-light column labels. Body cells use ink-2
            (#6B7280) — CNC's --p-text-muted — instead of the darker
            ink (#0B1E3D) so rows read as pharmacy data, not console
            logs. Interactive links inside cells still opt in to
            text-primary for the teal call-to-action tint. */}
        <thead className="bg-bg text-[10px] uppercase tracking-wide text-ink-3">
          <tr>
            {columns.map((col) => {
              const isActive = sort?.key === col.key;
              const indicator = !col.sortable
                ? null
                : !isActive
                  ? '↕'
                  : sort?.dir === 'asc'
                    ? '↑'
                    : '↓';
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={
                    !col.sortable
                      ? undefined
                      : isActive
                        ? sort?.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                  }
                  className={`px-3.5 py-2 font-semibold ${col.className ?? ''}`}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => cycleSort(col.key)}
                      className={
                        'inline-flex items-center gap-1 uppercase tracking-wide transition-colors ' +
                        (isActive
                          ? 'text-ink-2 hover:text-ink'
                          : 'text-ink-3 hover:text-ink-2')
                      }
                    >
                      <span>{col.header}</span>
                      <span
                        aria-hidden="true"
                        className={
                          'text-[9px] leading-none ' +
                          (isActive ? 'opacity-100' : 'opacity-40')
                        }
                      >
                        {indicator}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const clickable = Boolean(onRowClick);
            const trClass = clickable
              ? 'border-t border-border text-ink-2 cursor-pointer hover:bg-bg-soft'
              : 'border-t border-border text-ink-2';
            // Skip onRowClick when the user clicked an interactive
            // descendant (input, button, link). Cell renderers don't
            // need to call stopPropagation — which would otherwise
            // break React's checkbox onChange delegation.
            const handleClick = onRowClick
              ? (e: MouseEvent<HTMLTableRowElement>) => {
                  const target = e.target as HTMLElement | null;
                  if (target && target.closest('input, button, a, label')) {
                    return;
                  }
                  onRowClick(row);
                }
              : undefined;
            return (
              <tr key={rowKey(row)} className={trClass} onClick={handleClick}>
                {columns.map((col) => (
                  <td key={col.key} className={`px-3.5 py-2 align-middle ${col.className ?? ''}`}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
